
import { useEffect, useRef, useState, memo, useCallback } from 'react';

const faqs = [
  {
    question: 'How quickly can you respond to a water damage emergency?',
    answer:
      "We offer 24/7 emergency response throughout Davis County and Northern Utah. Our team typically arrives on-site within 60 minutes of your call. The faster we respond, the more damage we can prevent — so don't hesitate to call us at (385) 340-6632 any time, day or night.",
  },
  {
    question: 'Do you work with insurance companies?',
    answer:
      'Yes! We work directly with all major insurance providers to make the claims process as smooth as possible. Our team documents every step of the restoration with detailed photos and moisture readings, and we can communicate directly with your adjuster to help expedite your claim.',
  },
  {
    question: 'How long does the water damage restoration process take?',
    answer:
      "The timeline depends on the severity of the damage. Minor water damage may be fully restored in 3–5 days, while more extensive flooding or structural damage can take 1–2 weeks. During your free assessment, we'll give you a clear timeline so you know exactly what to expect.",
  },
  {
    question: 'Is mold a concern after water damage?',
    answer:
      "Absolutely. Mold can begin growing within 24–48 hours of water exposure. That's why rapid response is critical. Our team uses industrial dehumidifiers, air movers, and antimicrobial treatments to thoroughly dry your property and prevent mold growth. If mold is already present, we offer full mold remediation services.",
  },
  {
    question: 'What areas do you serve?',
    answer:
      "We proudly serve Davis County and the surrounding Northern Utah communities, including Kaysville, Clearfield, Layton, Farmington, Syracuse, Bountiful, Centerville, and more. If you're unsure whether you're in our service area, give us a call and we'll let you know.",
  },
  {
    question: 'Are your technicians certified?',
    answer:
      "Yes. All of our technicians are IICRC-certified (Institute of Inspection, Cleaning and Restoration Certification). This means they've completed rigorous training in water damage restoration, mold remediation, and structural drying — so you can trust that your property is in expert hands.",
  },
  {
    question: 'How much does water damage restoration cost?',
    answer:
      "Costs vary depending on the extent of the damage, the type of water involved, and the size of the affected area. We provide free, no-obligation assessments so you'll have a clear understanding of the scope and cost before any work begins. In many cases, your homeowner's insurance will cover most or all of the restoration.",
  },
  {
    question: 'What should I do while waiting for your team to arrive?',
    answer:
      "If it's safe to do so, turn off the water source and electricity in the affected area. Move valuables and furniture away from standing water. Avoid walking through contaminated water, especially if it may be sewage. Our team will guide you over the phone with specific steps while we're on our way.",
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const FAQItem = memo(({ faq, index, isOpen, onToggle }: { 
  faq: typeof faqs[0]; 
  index: number; 
  isOpen: boolean; 
  onToggle: () => void;
}) => (
  <div className={`border-b border-gray-200 ${index === 0 ? 'border-t' : ''}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 sm:py-6 text-left cursor-pointer group"
    >
      <span className="text-lg sm:text-xl font-semibold text-gray-900 pr-4 group-hover:text-sky-600 transition-colors">
        {faq.question}
      </span>
      <span
        className={`w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-300 ${
          isOpen ? 'bg-sky-600 rotate-180' : 'bg-gray-100 group-hover:bg-sky-100'
        }`}
      >
        <i
          className={`ri-arrow-down-s-line text-xl sm:text-2xl transition-colors ${
            isOpen ? 'text-white' : 'text-gray-500 group-hover:text-sky-600'
          }`}
        ></i>
      </span>
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-96 pb-5 sm:pb-6' : 'max-h-0'
      }`}
    >
      <p className="text-lg sm:text-xl leading-relaxed pr-12 text-gray-600">{faq.answer}</p>
    </div>
  </div>
));

FAQItem.displayName = 'FAQItem';

function FAQ() {
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    const el = sectionRef.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const toggle = useCallback((index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section id="faq" ref={sectionRef} className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-sky-600 text-xl font-semibold mb-2">Common Questions</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            Get answers to the most common questions about our water damage restoration and disaster
            cleanup services in Utah.
          </p>
        </div>

        <div
          className={`max-w-4xl mx-auto transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => toggle(index)}
            />
          ))}
        </div>

        <div
          className={`mt-10 sm:mt-16 text-center transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-gray-50 rounded-2xl p-6 sm:p-10 max-w-4xl mx-auto">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-question-answer-line text-2xl sm:text-3xl text-sky-600"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Still Have Questions?</h3>
            <p className="text-gray-600 text-lg sm:text-xl mb-6 max-w-lg mx-auto">
              Our team is here to help. Reach out anytime and we&apos;ll get back to you right away.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <button
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-sky-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </button>
              <a
                href="tel:3853406632"
                className="w-full sm:w-auto text-center border border-gray-300 text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>Call (385) 340-6632
              </a>
            </div>
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </section>
  );
}

export default memo(FAQ);
